import {Box, createStyles, makeStyles, Theme, Typography} from '@material-ui/core';
import clsx from 'clsx';
import React, {FC, useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {localized} from '../../../i18n/i18n';
import {energyConsumptionSlice} from '../../../state/features/energy-consumption/energy-consumption-slice';
import {AppState} from '../../../state/store';
import {
  basicCardBgColor,
  primaryColor,
  tooltipPrimaryTextColor,
  tooltipSecondaryTextColor,
} from '../../../styles/color-constants';
import {h6} from '../../../styles/font-size-constants';
import {BasicCard} from '../../../view/components/default-components/basic-card/basic-card';
import {BasicTooltip} from './basic-tooltip';
import {
  getConnectionIdsInPeriod,
  getOneYearPriorSortingDays,
  isUnitContractInPeriod,
} from '../../../state/features/energy-consumption/energy-consumption-helper';
import moment from 'moment';
import {DateFormat} from '../../../utilities/constants';
import {IUnitDto} from '../../../api/api';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sortFlexbox: {
      cursor: 'pointer',
      margin: 6,
      padding: 0,
      display: 'flex',
      textAlign: 'center',
      alignSelf: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#3333',
        color: '#FFF',
      },
    },
    disabled: {
      backgroundColor: '#3333',
      color: '#FFF',
    },
    selected: {
      cursor: 'pointer',
      backgroundColor: primaryColor,
      color: basicCardBgColor,
      '&:hover': {
        backgroundColor: primaryColor,
        color: basicCardBgColor,
      },
    },
    toolTipValueText: {
      fontWeight: 'bold',
      color: tooltipPrimaryTextColor,
    },
    toolTipDateText: {
      fontSize: h6,
      color: tooltipSecondaryTextColor,
    },
    toolTipBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
  }),
);

interface PropsFromParent {
  numberOfUnitsCallback: (number: number) => void;
}

export const ConsumptionUnits: FC<PropsFromParent> = React.memo(({children, ...props}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const firstUpdate = useRef(true);
  const {
    selectedMeasuringPointIds,
    units,
    sortingDays,
    idsInPeriod,
    idsInPeriodOneYearPrior,
    yearPriorSelected,
  } = useSelector((store: AppState) => store.energyConsumptionReducer);

  const handleCheckSinglePressed = useCallback(
    (newId: string) => {
      if (selectedMeasuringPointIds.includes(newId)) {
        let newIds = selectedMeasuringPointIds.filter((item) => {
          return item !== newId;
        });
        dispatch(energyConsumptionSlice.actions.setSelectedMeasuringPointIds(newIds));
      } else {
        let newIds: string[] = [...selectedMeasuringPointIds, newId];
        dispatch(energyConsumptionSlice.actions.setSelectedMeasuringPointIds(newIds));
      }
    },
    [dispatch, selectedMeasuringPointIds],
  );

  const unitsToShow = useMemo(() => {
    return units
      .filter((unit, i, unitList) => unitList.findIndex((t) => t.connectionid === unit.connectionid) === i)
      .sort((a, b) => (a.street! < b.street! ? -1 : a.houseNum! > b.houseNum! ? 1 : 0));
  }, [units]);

  const combinedUnitIds = useMemo(() => {
    return [...idsInPeriod, idsInPeriodOneYearPrior];
  }, [idsInPeriod, idsInPeriodOneYearPrior]);

  const isUnitActive = useCallback(
    (unit: IUnitDto) => {
      if (!combinedUnitIds.includes(unit.connectionid!)) {
        return false;
      }

      if (yearPriorSelected) {
        const oneYearPriorSortingDays = getOneYearPriorSortingDays(sortingDays);
        return isUnitContractInPeriod(unit, sortingDays) || isUnitContractInPeriod(unit, oneYearPriorSortingDays);
      }

      return true;
    },
    [combinedUnitIds, yearPriorSelected, sortingDays],
  );

  // First time select all units
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;

      dispatch(
        energyConsumptionSlice.actions.setSelectedMeasuringPointIds(getConnectionIdsInPeriod(units, sortingDays)),
      );
      dispatch(
        energyConsumptionSlice.actions.setMeasuringPointIdsInPeriod(getConnectionIdsInPeriod(units, sortingDays)),
      );
    }
  }, [dispatch, units, sortingDays]);

  useEffect(() => {
    props.numberOfUnitsCallback(getConnectionIdsInPeriod(unitsToShow, sortingDays).length);
  }, [unitsToShow, props, sortingDays]);

  return (
    <Box maxHeight={440} overflow={'auto'}>
      {unitsToShow &&
        unitsToShow.map((unit, index) => {
          return (
            <BasicTooltip
              key={index}
              placement="right"
              title={
                <Box className={classes.toolTipBox}>
                  <Typography className={classes.toolTipValueText}>
                    {isUnitActive(unit) ? unit.eanId : localized('YourContractDoseNotCoverSelectedPeriod')}
                  </Typography>
                  <Typography className={classes.toolTipDateText}>
                    {isUnitActive(unit)
                      ? localized('EanNumber')
                      : moment(unit.contractStartDate).format(DateFormat) +
                        ' - ' +
                        moment(unit.contractEndDate).format(DateFormat)}
                  </Typography>
                </Box>
              }>
              <BasicCard
                className={clsx(
                  classes.sortFlexbox,
                  selectedMeasuringPointIds.includes(unit.connectionid!) ? classes.selected : null,
                  !isUnitActive(unit) && classes.disabled,
                )}
                onClick={() => isUnitActive(unit) && handleCheckSinglePressed(unit.connectionid!)}>
                {unit.street + ' ' + unit.houseNum}
              </BasicCard>
            </BasicTooltip>
          );
        })}
    </Box>
  );
});
